/**
 * Auth Event Broadcasting
 * 
 * Syncs authentication events across multiple tabs/windows
 * Uses BroadcastChannel API with localStorage fallback
 */

export type AuthEventType = 'auth:login' | 'auth:refresh' | 'auth:logout';

export interface AuthEventDetail {
  type: AuthEventType;
  timestamp: number;
  reason?: string;
  data?: any;
}

const CHANNEL_NAME = 'osa_auth_channel';
const STORAGE_KEY = 'osa_auth_event';

/**
 * Check if BroadcastChannel is supported
 */
function isBroadcastChannelSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

/**
 * Auth Event Broadcaster
 */
class AuthEventBroadcaster {
  private channel: BroadcastChannel | null = null;
  private listeners: Array<(detail: AuthEventDetail) => void> = [];
  private storageListener: ((e: StorageEvent) => void) | null = null;

  constructor() {
    if (isBroadcastChannelSupported()) {
      this.initBroadcastChannel();
    } else {
      this.initStorageFallback();
    }
  }

  /**
   * Initialize BroadcastChannel
   */
  private initBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      
      this.channel.onmessage = (event: MessageEvent<AuthEventDetail>) => {
        // console.log('📡 Received auth event via BroadcastChannel:', event.data);
        this.notifyListeners(event.data);
      };
      
      // console.log('✅ BroadcastChannel initialized');
    } catch (error) {
      console.error('Failed to initialize BroadcastChannel:', error);
      // Fallback to localStorage
      this.initStorageFallback();
    }
  }

  /**
   * Initialize localStorage fallback for older browsers
   */
  private initStorageFallback(): void {
    // console.log('⚠️  Using localStorage fallback for auth events');
    
    this.storageListener = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const detail: AuthEventDetail = JSON.parse(e.newValue);
          // console.log('📡 Received auth event via localStorage:', detail);
          this.notifyListeners(detail);
        } catch (error) {
          console.error('Failed to parse storage event:', error);
        }
      }
    };
    
    window.addEventListener('storage', this.storageListener);
  }

  /**
   * Broadcast an auth event to all tabs
   */
  broadcast(type: AuthEventType, reason?: string, data?: any): void {
    const detail: AuthEventDetail = {
      type,
      timestamp: Date.now(),
      reason,
      data,
    };

    // console.log('📡 Broadcasting auth event:', detail);

    if (this.channel) {
      // Use BroadcastChannel
      this.channel.postMessage(detail);
    } else {
      // Use localStorage fallback
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(detail));
        // Clean up immediately to allow repeated events
        setTimeout(() => {
          localStorage.removeItem(STORAGE_KEY);
        }, 100);
      } catch (error) {
        console.error('Failed to broadcast via localStorage:', error);
      }
    }
  }

  /**
   * Subscribe to auth events
   */
  subscribe(listener: (detail: AuthEventDetail) => void): () => void {
    // console.log('📡 Subscribing to auth events');
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(detail: AuthEventDetail): void {
    // console.log('📡 Notifying', this.listeners.length, 'listener(s)');
    this.listeners.forEach(listener => {
      try {
        listener(detail);
      } catch (error) {
        console.error('Error in auth event listener:', error);
      }
    });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
    
    this.listeners = [];
  }
}

// Singleton instance
let broadcaster: AuthEventBroadcaster | null = null;

/**
 * Get or create the broadcaster instance
 */
export function getAuthBroadcaster(): AuthEventBroadcaster {
  if (!broadcaster) {
    broadcaster = new AuthEventBroadcaster();
  }
  return broadcaster;
}

/**
 * Broadcast login event
 */
export function broadcastLogin(data?: any): void {
  getAuthBroadcaster().broadcast('auth:login', undefined, data);
}

/**
 * Broadcast token refresh event
 */
export function broadcastRefresh(): void {
  getAuthBroadcaster().broadcast('auth:refresh');
}

/**
 * Broadcast logout event
 */
export function broadcastLogout(reason?: string): void {
  getAuthBroadcaster().broadcast('auth:logout', reason);
}

/**
 * Subscribe to auth events
 */
export function subscribeToAuthEvents(
  listener: (detail: AuthEventDetail) => void
): () => void {
  return getAuthBroadcaster().subscribe(listener);
}

